import React, { PropsWithChildren } from 'react';
import { useUpdates } from 'expo-updates';

import { theme } from '@sg/garnish';

import { useLaunchDarkly } from '@order/LaunchDarkly';

import {
  ErrorFallback,
  ForceUpdateMode,
  LoadingAnimation,
  MaintenanceMode,
  useIsForceUpdateModeEnabled,
  useIsMaintenanceModeEnabled,
} from './components';
import { use3rdPartyIntegrations, useAppFonts, useSession, useStartOtaObserver } from './hooks';
import { AppContent } from './App.content';

// ────────────────────────────────────────────────────────────────────────────────

/**
 * Conditionally renders the app by detecting possible blocks
 * (app fonts loading, "Force Update" mode, "Maintenance" mode, and so on).
 */
export const App = (props: PropsWithChildren) => {
  const { children } = props;

  const appFontsLoaded = useAppFonts();
  const { sessionReady, sessionError } = useSession();
  const { isLaunchDarklyReady, isLaunchDarklyTargetingContextSet } = useLaunchDarkly();
  const isMaintenanceModeEnabled = useIsMaintenanceModeEnabled();
  const isForceUpdatedModeEnabled = useIsForceUpdateModeEnabled();
  const { isRestarting } = useUpdates();

  use3rdPartyIntegrations();
  useStartOtaObserver();

  // ─────────────────────────────────────────────────────────────────────

  const isLoading =
    !appFontsLoaded ||
    !sessionReady ||
    !isLaunchDarklyReady ||
    !isLaunchDarklyTargetingContextSet ||
    isRestarting;

  if (sessionError) return <ErrorFallback error={sessionError} />;

  if (isLoading) {
    return <LoadingAnimation withNavbarOffset backgroundColor={theme.colors.APP_BACKGROUND} />;
  }

  if (isForceUpdatedModeEnabled) return <ForceUpdateMode />;

  if (isMaintenanceModeEnabled) return <MaintenanceMode />;

  return <AppContent>{children}</AppContent>;
};
